import { ReactNode, useState } from 'react';
import { Close, Search } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { Row } from 'further-ui/components/Layout';

interface Option<T> {
  label: string;
  chipLabel?: string;
  renderLabel?: ReactNode;
  value: T;
}

interface Props<T> {
  backgroundColor?: string;
  name?: string;
  searchFieldPlaceholder?: string;
  placeholder?: string;
  onReset?: () => void;
  value: Array<T>;
  onChange: (value: Array<T>) => void;
  error?: string;
  options: Array<Option<T>>;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  emptyMessage?: string;
}

const StyledChip = styled(Chip)(({ theme }) => ({
  '& .MuiChip-label': {
    color: theme.typography.h6.color,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const ChipsBox = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  '&& .MuiSelect-select': {
    padding: theme.spacing(0, theme.spacingSizes.xxs),
  },
}));

const SearchWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(theme.spacingSizes.xxs),
}));

const LabelWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  paddingRight: theme.spacing(theme.spacingSizes.xs),
}));

const EmptyMessage = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(theme.spacingSizes.xs),
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs, 0),
}));

const MultipleAutocomplete = <T,>({
  name,
  fullWidth = true,
  size = 'small',
  placeholder = '',
  searchFieldPlaceholder = 'Search',
  options,
  value,
  onChange,
  error,
  disabled,
  emptyMessage = 'No options available',
}: Props<T>): JSX.Element => {
  const [search, setSearch] = useState('');

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase()),
  );

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    onChange(event.target.value as Array<T>);
  };

  const handleClose = () => {
    setSearch('');
  };

  return (
    <FormControl fullWidth={fullWidth} size={size} error={!!error}>
      <StyledSelect
        multiple
        name={name}
        value={value}
        onChange={handleChange}
        onClose={handleClose}
        disabled={disabled}
        size="small"
        displayEmpty={!!placeholder}
        renderValue={(selected) => {
          const selectedArr = selected as Array<T>;
          if (selectedArr.length === 0) {
            return <span>{placeholder}</span>;
          }
          return (
            <ChipsBox>
              {selectedArr.map((val) => {
                const option = options.find((o) => o.value === val);
                return (
                  <StyledChip
                    key={String(val)}
                    label={option?.chipLabel ?? option?.label ?? String(val)}
                    size="small"
                    deleteIcon={
                      <Close onMouseDown={(e) => e.stopPropagation()} />
                    }
                    onDelete={() => onChange(value.filter((v) => v !== val))}
                  />
                );
              })}
            </ChipsBox>
          );
        }}
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            sx: (theme) => ({
              padding:
                filteredOptions.length > 0
                  ? theme.spacing(
                      theme.spacingSizes.sm,
                      theme.spacingSizes.sm,
                      theme.spacingSizes.xs,
                      theme.spacingSizes.sm,
                    )
                  : theme.spacing(theme.spacingSizes.sm),
            }),
          },
        }}
      >
        {options.length === 0 && (
          <Typography
            variant="body2"
            onClickCapture={(e) => e.stopPropagation()}
          >
            {emptyMessage}
          </Typography>
        )}
        {options.length > 0 && (
          <SearchWrapper onClickCapture={(e) => e.stopPropagation()}>
            <TextField
              size="small"
              autoFocus
              fullWidth
              placeholder={searchFieldPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </SearchWrapper>
        )}
        {options.length > 0 && filteredOptions.length === 0 && (
          <EmptyMessage
            variant="body2"
            onClickCapture={(e) => e.stopPropagation()}
          >
            {emptyMessage}
          </EmptyMessage>
        )}
        {filteredOptions.map((option) => (
          <StyledMenuItem
            key={String(option.value)}
            value={String(option.value)}
          >
            <Row fullWidth centered spacing="xs">
              <Checkbox size="small" checked={value.includes(option.value)} />
              <LabelWrapper>{option.renderLabel ?? option.label}</LabelWrapper>
            </Row>
          </StyledMenuItem>
        ))}
      </StyledSelect>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default MultipleAutocomplete;
